import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";

export interface PageDomainRecord {
  id: string;
  domain: string;
  status: "pending" | "verifying" | "active" | "failed";
  sslStatus: PageDomainSslStatus | null;
  verifiedAt: string | null;
}

export type PageDomainSslStatus =
  | "pending"
  | "pending_validation"
  | "pending_issuance"
  | "active"
  | "expired"
  | "failed";

export type PageDomainHealthIssueSeverity = "critical" | "warning" | "info";

export interface PageDomainHealthIssue {
  severity: PageDomainHealthIssueSeverity;
  code: string;
  message: string;
}

export interface PageDomainHealthRecord {
  type: string;
  name: string;
  value: string;
  ttl: number;
  priority?: number;
}

export interface PageDomainHealthProviderSignal {
  provider: string;
  source: string;
  value: string;
}

export interface PageDomainHealthScan {
  healthScore: number;
  scannedAt: string;
  aRecords: { found: boolean; records: PageDomainHealthRecord[] };
  aaaaRecords: { found: boolean; records: PageDomainHealthRecord[] };
  mxRecords: { found: boolean; records: PageDomainHealthRecord[] };
  nameservers: { found: boolean; records: PageDomainHealthRecord[] };
  caaRecords: { found: boolean; records: PageDomainHealthRecord[] };
  providerSignals: PageDomainHealthProviderSignal[];
  issues: PageDomainHealthIssue[];
}

export interface PageDomainHealth {
  domainId: string;
  domain: string;
  status: string;
  sslStatus: PageDomainSslStatus | null;
  dnsResolved: boolean;
  lastChecked: string;
  /** Rich DOM-B scan payload — present when the api-pages worker has the
   * @ease/dns-scanner wired into checkHealth. UIs that only consume the
   * legacy boolean continue to work without changes. */
  scan?: PageDomainHealthScan;
}

export interface NameserverTransferRecordInput {
  type: "A" | "AAAA" | "CNAME" | "MX" | "TXT" | "SRV" | "CAA" | "NS" | "SOA";
  name: string;
  content: string;
  ttl?: number;
  priority?: number;
  proxied?: boolean;
}

export interface NameserverTransferPlan {
  domainId: string;
  domain: string;
  transferMode: "operator_assisted_ns_transfer";
  cloudflareNameservers: string[];
  nameserverChange: {
    registrar: string;
    steps: string[];
  };
  importPlan: {
    totalInputRecords: number;
    importableRecords: NameserverTransferRecordInput[];
    skippedRecords: Array<{
      record: NameserverTransferRecordInput;
      reason: string;
    }>;
  };
  zeroDowntimeChecklist: string[];
  verification: {
    expectedNsRecords: string[];
    healthEndpoint: string;
    manualCheck: string;
  };
  automationStatus: {
    zoneCreateMounted: boolean;
    dnsImportMounted: boolean;
    nsVerificationMounted: boolean;
  };
  warnings: string[];
}

interface RegisteredDomainEnvelope {
  domain: PageDomainRecord;
  cnameTarget: string;
}

export function usePageDomains() {
  const [domains, setDomains] = useState<PageDomainRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDomains = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch<{ data: PageDomainRecord[] }>("/crm/pages/domains");
      setDomains(response.data ?? []);
    } catch {
      setDomains([]);
      setError("Failed to load mounted custom domains.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchDomains();
  }, [fetchDomains]);

  return { domains, isLoading, error, refetch: fetchDomains };
}

export async function createPageDomain(domain: string) {
  const response = await apiFetch<{ data: RegisteredDomainEnvelope }>("/crm/pages/domains", {
    method: "POST",
    body: JSON.stringify({ domain }),
  });

  return response.data;
}

export async function verifyPageDomain(domainId: string) {
  const response = await apiFetch<{ data: PageDomainRecord }>(`/crm/pages/domains/${domainId}/verify`, {
    method: "POST",
  });

  return response.data;
}

export async function getPageDomainHealth(domainId: string) {
  const response = await apiFetch<{ data: PageDomainHealth }>(`/crm/pages/domains/${domainId}/health`);

  return response.data;
}

export async function createNameserverTransferPlan(
  domainId: string,
  input: {
    registrar?: string;
    assignedCloudflareNameservers?: string[];
    existingRecords?: NameserverTransferRecordInput[];
  },
) {
  const response = await apiFetch<{ data: NameserverTransferPlan }>(
    `/crm/pages/domains/${domainId}/nameserver-transfer/plan`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );

  return response.data;
}

export async function removePageDomain(domainId: string) {
  await apiFetch(`/crm/pages/domains/${domainId}`, {
    method: "DELETE",
  });
}

/**
 * DOM-004 Phase 3 — coexistence-mode DNS instruction client.
 *
 * Mirrors the Phase 1 `CoexistenceInstructionStep` shape verbatim so the
 * settings UI can render the 4-step plan returned by the Phase 2 route
 * `GET /domains/:id/coexistence-instructions?provider=<slug>`.
 *
 * Provider slugs MUST match the closed `COEXISTENCE_PROVIDERS` tuple at
 * `packages/dns-scanner/src/coexistence-instructions.ts`. Unknown slugs
 * collapse to `generic` server-side (Phase 2 contract) — the UI does
 * not need to defend against that, but it does narrow inputs locally to
 * avoid sending nonsense over the wire.
 */
export const COEXISTENCE_PROVIDER_SLUGS = [
  "godaddy",
  "namecheap",
  "cloudflare",
  "google_domains",
  "squarespace_domains",
  "generic",
] as const;

export type CoexistenceProviderSlug =
  (typeof COEXISTENCE_PROVIDER_SLUGS)[number];

export const COEXISTENCE_RECORD_KIND_SLUGS = [
  "ownership_txt",
  "web_cname",
  "email_mx_warning",
  "spf_merge_warning",
] as const;

export type CoexistenceRecordKindSlug =
  (typeof COEXISTENCE_RECORD_KIND_SLUGS)[number];

export interface CoexistenceInstructionStepView {
  kind: CoexistenceRecordKindSlug;
  heading: string;
  body: string;
  recordType: "TXT" | "CNAME" | "MX" | "NOTE";
  recordName: string;
  recordValue: string;
  provider: CoexistenceProviderSlug;
  navigationHint: string;
}

export interface CoexistenceInstructionPlan {
  domainId: string;
  domain: string;
  provider: CoexistenceProviderSlug;
  apexDomain: string;
  subdomain: string;
  cnameTarget: string;
  steps: CoexistenceInstructionStepView[];
}

export async function getCoexistenceInstructions(
  domainId: string,
  provider: CoexistenceProviderSlug,
): Promise<CoexistenceInstructionPlan> {
  const response = await apiFetch<{ data: CoexistenceInstructionPlan }>(
    `/crm/pages/domains/${domainId}/coexistence-instructions?provider=${provider}`,
  );

  return response.data;
}
